import React, { useState } from "react";

import { CheckIcon, MinusIcon } from "../../icons_v2/general";
import { Box, HStack, Pressable, Text } from "../../primitive";

type CheckboxBaseProps = {
  id?: string;
  testID?: string;
  isIndeterminate?: boolean;
  isChecked?: boolean;
  onPress?: () => void;
  isDisabled?: boolean;
  onBlur?: () => void;
  onFocus?: () => void;
  /** Set to false to hide the entire checkbox from the accessibility tree (e.g. when a parent element already carries the checkbox role). */
  accessible?: boolean;
};

type CheckboxProps = CheckboxBaseProps &
  (
    | {
        /** Forwarded as both aria-label and accessibilityLabel. Falls back to label text when omitted. */
        ariaLabel?: string;
        label?: string;
      }
    | {
        /** Required when label is JSX because accessibility labels must be text. */
        ariaLabel: string;
        label: React.JSX.Element;
      }
  );

export const CheckBox = ({
  id,
  label,
  ariaLabel,
  testID,
  isIndeterminate,
  isChecked,
  onPress,
  isDisabled,
  onBlur,
  onFocus,
  accessible,
}: CheckboxProps) => {
  const resolvedAccessibilityLabel =
    ariaLabel ?? (typeof label === "string" ? label : undefined);
  const [isHovered, setIsHovered] = useState(false);
  const [isPressed, setIsPressed] = useState(false);

  const bgColor = isDisabled
    ? "button-disabled-surface-neutral"
    : isChecked || isIndeterminate
    ? isHovered
      ? "button-main-surface-hover"
      : isPressed
      ? "button-main-surface-pressed"
      : "button-main-surface-neutral"
    : "surface-primary";

  const iconColor = isDisabled
    ? "icon-disabled"
    : isChecked || isIndeterminate
    ? "icon-inverse"
    : "icon-default";

  const textColor = isDisabled ? "text-disabled" : "text-primary";

  const isInteractive = !!onPress;
  // When accessible={false} the visual widget must be fully inert — use Box so
  // no role, tabindex, or aria-checked appears in the DOM for AT to discover.
  const isEffectivelyInteractive = isInteractive && accessible !== false;
  const CheckBoxPressable = isEffectivelyInteractive ? Pressable : Box;

  return (
    <HStack gap={2} alignItems="center">
      <CheckBoxPressable
        id={id}
        testID={testID}
        {...{
          "aria-label": resolvedAccessibilityLabel,
          accessibilityLabel: resolvedAccessibilityLabel,
        }}
        {...(isEffectivelyInteractive
          ? {
              accessibilityRole: "checkbox" as const,
              accessibilityState: {
                checked: isChecked ?? false,
                disabled: isDisabled ?? false,
              },
              "aria-checked": isChecked ?? false,
            }
          : {})}
        onPress={isEffectivelyInteractive ? onPress : undefined}
        disabled={isEffectivelyInteractive ? isDisabled : undefined}
        onBlur={isEffectivelyInteractive ? onBlur : undefined}
        onFocus={isEffectivelyInteractive ? onFocus : undefined}
        onPointerEnter={
          isEffectivelyInteractive ? () => setIsHovered(true) : undefined
        }
        onPointerLeave={
          isEffectivelyInteractive ? () => setIsHovered(false) : undefined
        }
        onPressIn={
          isEffectivelyInteractive ? () => setIsPressed(true) : undefined
        }
        onPressOut={
          isEffectivelyInteractive ? () => setIsPressed(false) : undefined
        }
        focusable={isEffectivelyInteractive ? undefined : false}
        borderWidth={1.5}
        borderRadius="2"
        borderColor="transparent"
        padding={".5"}
      >
        <Box
          justifyContent="center"
          alignItems="center"
          width={18}
          height={18}
          borderRadius="1"
          borderWidth={1.5}
          borderColor={
            isChecked || isIndeterminate ? "transparent" : "outline-default"
          }
          bg={bgColor}
        >
          {isChecked ? (
            <CheckIcon color={iconColor} />
          ) : isIndeterminate ? (
            <MinusIcon color={iconColor} />
          ) : null}
        </Box>
      </CheckBoxPressable>

      {label && (
        <Text variant="mainBaseMedium" color={textColor} selectable={false}>
          {label}
        </Text>
      )}
    </HStack>
  );
};
